import { Button, Form, message, Steps } from 'antd';
import { useTranslation } from '../../../i18n/i18n';
import React, { useState } from 'react';
import './CreateInstallationLocationModal.scss';
import { PlusOutlined } from '@ant-design/icons';
import { ConnectError } from '@connectrpc/connect';
import ModalCustom from '../../utils/ModalCustom/ModalCustom';
import useTenantStore, { ALL_TENANTS_VALUE } from '../../../stores/tenant.store';
import FormGeneralData from './FormGeneralData';
import FormBuildingData, { InstallationLocationBuildingFormValues } from './FormBuildingData';

export type InstallationLocationFormValues = {
  erpId: string;
  city: string;
  name: string;
  id: string;
  address: string;
  postalCode: string;
  lat: string;
  lon: string;
  tenant: string;
  userAdmin: string;
};

export type CreateInstallationLocationFormValues = InstallationLocationFormValues &
  InstallationLocationBuildingFormValues;

export type CreateLocationParams = {
  name: string;
  erpId: string;
  address: string;
  cp: string;
  city: string;
  country: string;
  latitude: number;
  longitude: number;
  tenantUuid: string;
  adminUuid: string;
  numberOfApartments?: number;
  numberOfFloors?: number;
  numberOfCommercialUnits?: number;
  buildingAreaM2?: number;
  averageAge?: number;
  numberOfPeople?: number;
  roofAreaM2?: number;
  hasRainwaterHarvesting?: boolean;
};

interface CreateInstallationLocationModalProps {
  onSave: (values: CreateLocationParams) => Promise<void>;
}

const CreateInstallationLocationModal = ({ onSave }: CreateInstallationLocationModalProps) => {
  const { t } = useTranslation('installation-location', { keyPrefix: 'modal' });
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [form] = Form.useForm<CreateInstallationLocationFormValues>();
  const [messageApi, contextHolder] = message.useMessage();
  const isAll = useTenantStore((state) => state.selectedTenant === ALL_TENANTS_VALUE);
  const selectedTenant = useTenantStore((state) => state.selectedTenant);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [step, setStep] = useState(0);

  const steps = [{ title: t('generalData') }, { title: t('buildingData') }];

  const handleSave = async () => {
    let rawValues: CreateInstallationLocationFormValues;
    try {
      rawValues = await form.validateFields();
    } catch {
      return;
    }
    try {
      setIsLoading(true);
      const lat = parseFloat(rawValues.lat);
      const lon = parseFloat(rawValues.lon);
      const tenantUuid = isAll ? rawValues.tenant : selectedTenant!;
      await onSave({
        name: rawValues.name,
        erpId: rawValues.id,
        address: rawValues.address,
        cp: rawValues.postalCode,
        city: rawValues.city,
        country: 'ES',
        latitude: lat,
        longitude: lon,
        tenantUuid,
        adminUuid: rawValues.userAdmin,
        numberOfApartments: rawValues.numberOfApartments,
        numberOfFloors: rawValues.numberOfFloors,
        numberOfCommercialUnits: rawValues.numberOfCommercialUnits,
        buildingAreaM2: rawValues.buildingAreaM2,
        averageAge: rawValues.averageAge,
        numberOfPeople: rawValues.numberOfPeople,
        roofAreaM2: rawValues.roofAreaM2,
        hasRainwaterHarvesting: rawValues.hasRainwaterHarvesting,
      });
      form.resetFields();
      setIsModalOpen(false);
      setStep(0);
    } catch (error) {
      if (error instanceof ConnectError) {
        console.error('Create location failed:', error.code, error.message, error.details, error.rawMessage);
      } else {
        console.error('Create location failed:', error);
      }
      messageApi.error(t('common:errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = async () => {
    try {
      await form.validateFields(['name', 'id', 'address', 'postalCode', 'city', 'lat', 'lon', 'tenant', 'userAdmin']);
      setStep(1);
    } catch {
      // errors are shown inline by the form fields
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setIsModalOpen(false);
    setStep(0);
  };

  return (
    <>
      {contextHolder}
      <Button type={'primary'} className={'OpenModalButton'} size={'middle'} onClick={() => setIsModalOpen(true)}>
        <PlusOutlined />
        {t('create')}
      </Button>
      <ModalCustom
        title={t('title')}
        description={t('modalDesc')}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        footer={
          <>
            {step === 0 && (
              <>
                <Button key="save" type={'primary'} onClick={handleNext}>
                  {t('next')}
                </Button>
              </>
            )}
            {step === 1 && (
              <>
                <Button key="cancel" onClick={handleCancel}>
                  {t('cancel')}
                </Button>
                <Button key="save" type={'primary'} onClick={handleSave} loading={isLoading}>
                  {t('save')}
                </Button>
              </>
            )}
          </>
        }
      >
        <Steps items={steps} current={step} />
        <Form form={form} layout="vertical" className={'Modal__form'}>
          <div style={{ display: step === 0 ? undefined : 'none' }}>
            <FormGeneralData form={form} isModalOpen={isModalOpen} />
          </div>
          <div style={{ display: step === 1 ? undefined : 'none' }}>
            <FormBuildingData />
          </div>
        </Form>
      </ModalCustom>
    </>
  );
};

export default CreateInstallationLocationModal;
